import React from "react";
import { DeleteGuide, GetGuides, GuideViewModel } from "./GuideStore";
import OrganizationContextSwitcher from "../organizations/ContextSwitcher";
import { Button } from "../components/Modal";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useUser } from "../account/AccountStore";

interface FolderNode {
    name: string;
    path: string;
    children: FolderNode[];
    guides: GuideViewModel[];
    isExpanded: boolean;
}

const GuidesIndex = () => {
    const [guides, setGuides] = React.useState([] as GuideViewModel[]);
    const [folderStructure, setFolderStructure] = React.useState({} as Record<string, boolean>);
    const user = useUser();

    React.useEffect(() => {
        GetGuides("").then((res) => setGuides(res.value));
    }, []);

    const deleteGuide = async (id: string) => {
        await DeleteGuide(id);
        GetGuides("").then((res) => setGuides(res.value));
    };

    const buildFolderStructure = (guides: GuideViewModel[]): FolderNode => {
        const root: FolderNode = {
            name: "root",
            path: "",
            children: [],
            guides: [],
            isExpanded: true,
        };

        guides.forEach((guide) => {
            const keyParts = guide.key ? guide.key.split(".") : [""];
            let currentNode = root;

            // Navigate/create folder structure
            for (let i = 0; i < keyParts.length - 1; i++) {
                const folderName = keyParts[i];
                const folderPath = keyParts.slice(0, i + 1).join(".");

                let childNode = currentNode.children.find((child) => child.name === folderName);
                if (!childNode) {
                    childNode = {
                        name: folderName,
                        path: folderPath,
                        children: [],
                        guides: [],
                        isExpanded: folderStructure[folderPath] !== false,
                    };
                    currentNode.children.push(childNode);
                }
                currentNode = childNode;
            }

            // Add guide to the appropriate folder
            currentNode.guides.push(guide);
        });

        return root;
    };

    const toggleFolder = (folderPath: string) => {
        setFolderStructure((prev) => ({
            ...prev,
            [folderPath]: !prev[folderPath],
        }));
    };

    const renderFolderNode = (node: FolderNode, depth: number = 0): React.ReactNode => {
        const hasContent = node.children.length > 0 || node.guides.length > 0;
        if (!hasContent && node.name !== "root") return null;

        return (
            <div key={node.path || "root"}>
                {node.name !== "root" && (
                    <div
                        className={`flex items-center py-2 px-4 cursor-pointer hover:bg-gray-50 border-l-2 border-transparent hover:border-blue-300 transition-colors`}
                        style={{ paddingLeft: `${depth * 20 + 16}px` }}
                        onClick={() => toggleFolder(node.path)}
                    >
                        <FontAwesomeIcon icon={["fad", node.isExpanded ? "folder-open" : "folder"]} className={`text-lg mr-3 ${node.isExpanded ? "text-blue-500" : "text-gray-400"}`} />
                        <span className="font-medium text-gray-700">{node.name}</span>
                        <FontAwesomeIcon icon={["fad", node.isExpanded ? "chevron-down" : "chevron-right"]} className="text-xs text-gray-400 ml-auto" />
                    </div>
                )}

                {(node.name === "root" || node.isExpanded) && (
                    <>
                        {/* Render child folders */}
                        {node.children.map((child) => renderFolderNode(child, depth + 1))}

                        {/* Render guides in this folder */}
                        {node.guides.map((guide) => (
                            <div
                                key={guide.id}
                                className={`flex items-center py-3 px-4 hover:bg-gray-50 border-l-2 border-transparent hover:border-green-300 transition-colors`}
                                style={{ paddingLeft: `${(depth + 1) * 20 + 16}px` }}
                            >
                                <div className="flex items-center flex-1 min-w-0">
                                    <FontAwesomeIcon icon={["fad", "file-alt"]} className="text-green-500 mr-3 flex-shrink-0" />
                                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                                        {guide.thumbnailURI && <img src={guide.thumbnailURI} alt={guide.name} className="w-16 h-10 object-cover rounded border border-gray-200 flex-shrink-0" />}
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm font-medium text-gray-900 truncate">{guide.name}</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center space-x-4 ml-4 flex-shrink-0">
                                    <a
                                        href={guide.guideURI}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center text-blue-600 hover:text-blue-500 text-sm space-x-2"
                                        title="View Guide"
                                    >
                                        <FontAwesomeIcon icon={["fad", "external-link"]} />
                                        <span>View Guide</span>
                                    </a>
                                    {user.hasPermission("manageGuides") && (
                                        <Link to={`/guides/${guide.id}`} title="Edit Guide">
                                            <Button variant="outline-secondary" size="sm">
                                                <FontAwesomeIcon icon={["fad", "edit"]} />
                                            </Button>
                                        </Link>
                                    )}
                                    {user.hasPermission("manageGuides") && (
                                        <Button variant="outline-danger" size="sm" onClick={() => deleteGuide(guide.id)} title="Delete Guide">
                                            <FontAwesomeIcon icon={["fad", "trash"]} />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </>
                )}
            </div>
        );
    };

    const folderTree = React.useMemo(() => buildFolderStructure(guides), [guides, folderStructure]);

    return (
        <div>
            <OrganizationContextSwitcher></OrganizationContextSwitcher>

            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header Section */}
                <div className="flex items-center justify-between pb-6 border-b border-gray-200 mb-6">
                    <div className="flex items-center gap-3">
                        <FontAwesomeIcon icon={["fad", "book-open"]} className="text-3xl text-purple-500" />
                        <div>
                            <h3 className="text-3xl font-bold text-gray-900">Guides</h3>
                            <p className="text-sm text-gray-600 mt-1">Manage and organize user guides</p>
                        </div>
                    </div>
                    {user.hasPermission("manageGuides") && (
                        <Link to="/guides/create">
                            <Button variant="primary">
                                <FontAwesomeIcon icon={["fad", "plus"]} className="mr-2" />
                                Create Guide
                            </Button>
                        </Link>
                    )}
                </div>

                {/* Guides Folder Structure */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    {guides.length === 0 ? (
                        <div className="px-6 py-12 text-center">
                            <FontAwesomeIcon icon={["fad", "books"]} className="text-4xl text-gray-300 mb-3" />
                            <p className="text-gray-500">No guides found</p>
                            <Link to="/guides/create" className="text-blue-600 hover:text-blue-500 text-sm mt-2 inline-block">
                                Create your first guide
                            </Link>
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-100">{renderFolderNode(folderTree)}</div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default GuidesIndex;
